import React, { useState, useEffect, useCallback } from 'react';
import ReactECharts from './Chart';
import { Box, List, ListItemButton, ListItemText, Typography, Button } from '@mui/material';

const colorPalette = ['#da291c', '#3cb17e', '#307fe2', '#9063cd', '#2cccd3', '#a2b2c8'];

const UsersGeography = ({ initialData, onDrillDown, onBackClick, onTopClick, currentLevel, levels, dataStack }) => {
  const [chartOptions, setChartOptions] = useState({});
  const [hoverData, setHoverData] = useState(null);
  const [seriesData, setSeriesData] = useState([]);

  // Mapping of original names to readable names
  const readableNames = {
    'geography': 'Geography',
    'region': 'Region',
    'area': 'Area',
    'district': 'District',
    'userrole': 'User Role',
    'l2manager': 'Level II Manager',
    'manager': 'Manager'
  };

  const getReadableName = (name) => readableNames[name] || name;

  const updateChartOptions = useCallback((data, level) => {
    console.log('Updating chart options:', { data, level });
    const currentLevelName = levels[level];
    const readableLevelName = getReadableName(currentLevelName);

    const groupedData = data.reduce((acc, item) => {
      const key = item[currentLevelName] || "(none)";  // Replace empty values with "(none)"
      if (!acc[key]) acc[key] = { value: 0, drilldown: {} };
      acc[key].value++;
      // Store all unique values for the next level
      levels.slice(level + 1).forEach(nextLevel => {
        if (item[nextLevel]) {
          acc[key].drilldown[nextLevel] = acc[key].drilldown[nextLevel] || new Set();
          acc[key].drilldown[nextLevel].add(item[nextLevel]);
        }
      });
      return acc;
    }, {});

    const total = Object.values(groupedData).reduce((sum, { value }) => sum + value, 0);
    const newSeriesData = Object.entries(groupedData).map(([name, { value, drilldown }]) => ({
      name,
      value,
      total,
      drilldown: Object.fromEntries(Object.entries(drilldown).map(([k, v]) => [k, Array.from(v)]))
    }));

    setSeriesData(newSeriesData);

    setChartOptions({
      color: colorPalette,
      tooltip: {
        show: false
      },
      series: [
        {
          name: readableLevelName,
          type: 'pie',
          radius: ['55%', '90%'],
          center: ['57%', '50%'],
          avoidLabelOverlap: false,
          label: {
            show: false
          },
          emphasis: {
            scale: true,
            scaleSize: 10,
            label: {
              show: false
            }
          },
          labelLine: {
            show: false
          },
          data: newSeriesData,
          itemStyle: {
            borderWidth: 1,
            borderColor: '#fff'
          }
        },
      ],
    });
  }, [levels]);

  useEffect(() => {
    if (initialData.length > 0) {
      console.log('Initial data changed:', initialData);
      updateChartOptions(initialData, currentLevel);
    }
  }, [initialData, currentLevel, updateChartOptions]);

  const handleChartClick = useCallback((params) => {
    console.log('Chart clicked:', params);
    const nextLevel = currentLevel + 1;
    if (nextLevel < levels.length) {
      const clickedName = params.name;
      console.log('Clicked name:', clickedName);
      const nextLevelName = levels[nextLevel];
      console.log('Next level name:', nextLevelName);
      const filteredData = initialData.filter((item) => 
        (item[levels[currentLevel]] || "(none)") === clickedName
      );
      console.log('Filtered data:', filteredData);
      onDrillDown(filteredData, nextLevel, nextLevelName);
    } else {
      console.log('Cannot drill down further, reached max level');
    }
  }, [currentLevel, initialData, levels, onDrillDown]);

  const handleMouseOver = useCallback((params) => {
    const item = seriesData.find(d => d.name === params.name);
    if (item) {
      const percentage = ((item.value / item.total) * 100).toFixed(2);
      setHoverData({
        name: item.name,
        value: item.value,
        percentage: percentage
      });
    }
  }, [seriesData]);

  const handleMouseOut = useCallback(() => {
    setHoverData(null);
  }, []);

  const onEvents = {
    'click': handleChartClick,
    'mouseover': handleMouseOver,
    'mouseout': handleMouseOut
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', flexWrap: 'wrap', gap: '4px' }}>
        <Button 
          onClick={onBackClick} 
          disabled={dataStack.length <= 1}
          variant="outlined"
          size="small"
          sx={{ 
            marginRight: '8px',
            color: dataStack.length > 1 ? 'black' : 'rgba(0, 0, 0, 0.26)',
            borderColor: dataStack.length > 1 ? 'black' : 'rgba(0, 0, 0, 0.12)',
            padding: '2px 8px',
            minWidth: '70px',
            fontSize: '0.75rem'
          }}
        >
          Back
        </Button>
        <Button 
          onClick={onTopClick} 
          disabled={dataStack.length <= 1}
          variant="outlined"
          size="small"
          sx={{ 
            color: dataStack.length > 1 ? 'black' : 'rgba(0, 0, 0, 0.26)',
            borderColor: dataStack.length > 1 ? 'black' : 'rgba(0, 0, 0, 0.12)',
            padding: '2px 8px',
            minWidth: '70px',
            fontSize: '0.75rem'
          }}
        >
          Clear
        </Button>
      </div>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, marginTop: '10px' }}>
        <Box sx={{ 
          width: '45%', 
          overflowY: 'auto', 
          borderRight: '1px solid #ccc',
          height: '90%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <List dense>
            {seriesData.map((item, index) => (
              <ListItemButton
                key={item.name}
                onClick={() => handleChartClick({ name: item.name })}
                onMouseEnter={() => handleMouseOver({ name: item.name })}
                onMouseLeave={handleMouseOut}
              >
                <Box 
                  sx={{ 
                    width: 16, 
                    height: 16, 
                    backgroundColor: colorPalette[index % colorPalette.length],
                    marginRight: 1
                  }} 
                />
                <ListItemText primary={item.name} />
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box sx={{ width: '70%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ReactECharts
              option={chartOptions}
              style={{ height: '100%', width: '100%' }}
              onEvents={onEvents}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ 
        height: '80px',
        display: 'flex', 
        flexDirection: 'column',  
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '0 10px',
        marginTop:'-10px'
      }}>
        {hoverData ? (
          <>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
              {hoverData.name}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
              {`${hoverData.value} (${hoverData.percentage}%)`}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
            
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default UsersGeography;